import { Guid } from '../../../store/model/guid';
import { AssertionException } from '../../../utils/log';
import { isQuiz, isSequencedFlowItem, Item, ItemHelper } from '@ael/core-api';
import * as Immutable from 'immutable';
import {StudentStore} from '../../../store/models/Store';
import {getItemByIdSelector} from '../../../store/reducers/items/itemsSelectors';

export class CompletedItemsStore {
  constructor(private _completedItems: Immutable.Set<Guid>, private _getItemById: (itemId: Guid) => Item) {}

  public isInProgress = (item: Item): boolean => {
    return this._completedItems.contains(IdentifierFactory(item, false).serialize());
  };

  public isFinished = (item: Item): boolean => {
    return this._completedItems.contains(IdentifierFactory(item, true).serialize());
  };

  addStartedItem = (itemId: Guid) => {
    const item = this._getItemById(itemId);
    const trackingIdentifier = IdentifierFactory(item, false);
    this._completedItems = this._completedItems.add(trackingIdentifier.serialize());
  };

  addFinishedItem = (itemId: Guid) => {
    const item = this._getItemById(itemId);
    const trackingIdentifier = IdentifierFactory(item, true);
    this._completedItems = this._completedItems.add(trackingIdentifier.serialize());
  };

  removeFinishedItem = (itemId: Guid) => {
    const item = this._getItemById(itemId);
    const trackingIdentifier = IdentifierFactory(item, true);
    this._completedItems = this._completedItems.remove(trackingIdentifier.serialize());
  };

  reset = () => {
    this._completedItems = this._completedItems.clear();
  };

  toJS = (): Immutable.Set<string> => {
    return this._completedItems;
  };
}

const IdentifierFactory = (item: Item, isCompleted: boolean): IItemIdentifier => {
  if (ItemHelper.isContentItem(item)) {
    return new ContentItemIdentifier(item.id);
  }

  if (isQuiz(item) || isSequencedFlowItem(item)) {
    if (!isCompleted) {
      return new ItemIdentifier(item.id);
    }
    return new CompletedItemIdentifier(item.id);
  }

  throw AssertionException('Unknown item for tracking identifier');
};

interface IItemIdentifier {
  serialize: () => string;
}

class ContentItemIdentifier implements IItemIdentifier {
  protected _itemId: Guid;
  constructor(itemId: Guid) {
    this._itemId = itemId;
  }

  serialize() {
    return this._itemId;
  }
}

class ItemIdentifier implements IItemIdentifier {
  protected _itemId: Guid;
  constructor(itemId: Guid) {
    this._itemId = itemId;
  }

  serialize() {
    return this._itemId;
  }
}

export const createCompletedItemsStoreFromState = (state: StudentStore) => {
  return new CompletedItemsStore(state.tracking.items.finishedItems, getItemByIdSelector(state));
};

export const completedSeqFlowPostfix = '-completed';

class CompletedItemIdentifier extends ItemIdentifier {
  serialize() {
    return this._itemId + completedSeqFlowPostfix;
  }
}
